import React from "react";
import { flushSync } from "react-dom";
import { Box, Typography } from "@mui/material";
import { SpecsInputField } from "./SpecsEditorFields/index";
import SpecsModelAutocompleteField from "./components/editors/SpecsModelAutocompleteField";
import SpecsModelInputField from "./components/editors/SpecsModelInputField";
import SpecsModelMultiAutocompleteField from "./components/editors/SpecsModelMultiAutocompleteField";
import SpecsModelNumberField from "./components/editors/SpecsModelNumberField";
import {
  getSelectedPositionForVersion,
  mutateSelectedPositionForVersion,
  syncLockedSideDimension,
} from "./positionEditorModel";
import type { VersionModel } from "./specsModel/VersionModel";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import { useSpecsModel, useSpecsModelSelector } from "./SpecsModelContext";

const DESIGN_STATE_FIELDS = [
  ["Min Width", "minWidthInches"],
  ["Max Width", "maxWidthInches"],
  ["Min Height", "minHeightInches"],
  ["Max Height", "maxHeightInches"],
  ["Increment", "increment"],
  ["Angle du plan", "planeAngle"],
] as const;

function compactStringOptions(values: unknown[]): string[] {
  return values.filter((value): value is string => typeof value === "string");
}

export default function SpecsPositionIdentitySection() {
  const specsModel = useSpecsModel();
  const {
    editor: {
      identity: { productVersion },
      selectPositions,
    },
    selectedPositionId,
  } = useSpecsEditorContextValue();
  const currentVersion = useSpecsModelSelector(
    (model) => model.getVersion(productVersion) ?? null
  );
  const selectedPositionPath = useSpecsModelSelector((model) => {
    const versionIndex = model.product.versions.findIndex(
      (version) => version.name === productVersion
    );
    const version = model.product.versions[versionIndex];

    if (!version) {
      return null;
    }

    const positionIndex = version.positions.findIndex(
      (position: any) => position?.id === selectedPositionId
    );

    if (positionIndex === -1) {
      return null;
    }

    return `window.specs.model.product.versions[${versionIndex}].positions[${positionIndex}]`;
  });

  const hasSelectedPosition = useSpecsModelSelector(
    (model) =>
      !!getSelectedPositionForVersion(model, productVersion, selectedPositionId)
  );
  const positionId = useSpecsModelSelector(
    (model) =>
      getSelectedPositionForVersion(model, productVersion, selectedPositionId)
        ?.id ?? ""
  );
  const positionIds = useSpecsModelSelector((model) =>
    compactStringOptions(
      model
        .getVersion(productVersion)
        ?.getPositions()
        .map((position: any) => position.id) ?? []
    )
  );
  const positionName = useSpecsModelSelector(
    (model) =>
      getSelectedPositionForVersion(model, productVersion, selectedPositionId)
        ?.designState?.positionName ?? ""
  );
  const groupNameOptions = useSpecsModelSelector((model) =>
    compactStringOptions(
      Array.from(
        new Set([
          ...(model.getVersion(productVersion)?.getPositionGroups() ?? []).map(
            (group: any) => group.name
          ),
          ...(model.getVersion(productVersion)?.getPositions() ?? []).map(
            (position: any) => position.designState?.groupName
          ),
        ])
      )
    ).sort()
  );
  const dropdownLabelOptions = useSpecsModelSelector((model) =>
    compactStringOptions(
      Array.from(
        new Set(
          (model.getVersion(productVersion)?.getPositions() ?? []).map(
            (position: any) => position.designState?.dropdownLabel
          )
        )
      )
    ).sort()
  );
  const decorationTypeOptions = useSpecsModelSelector((model) =>
    compactStringOptions(
      Array.from(
        new Set(
          (model.getVersion(productVersion)?.getPositions() ?? []).flatMap(
            (position: any) => [
              ...(position.allowedDecorationTypes ?? []),
              ...(position.deprecatedDecorationTypes ?? []),
              ...((position.generator?.sides ?? []).flatMap((side: any) =>
                (side.zIndexByType ?? []).map((item: any) => item.type)
              ) ?? []),
            ]
          )
        )
      )
    ).sort()
  );

  const mutateSelectedPosition = React.useCallback(
    (mutator: (position: any, version: VersionModel) => void) => {
      mutateSelectedPositionForVersion(
        specsModel,
        productVersion,
        selectedPositionId,
        mutator
      );
    },
    [productVersion, selectedPositionId, specsModel]
  );
  const [positionIdDraft, setPositionIdDraft] = React.useState(positionId);

  React.useEffect(() => {
    setPositionIdDraft(positionId);
  }, [positionId]);

  const positionIdDraftTrimmed = positionIdDraft.trim();
  const isPositionIdBlank = !positionIdDraftTrimmed;
  const isPositionIdChanged = positionIdDraftTrimmed !== positionId;
  const isPositionIdDuplicate =
    isPositionIdChanged && positionIds.includes(positionIdDraftTrimmed);
  let positionIdError = "";

  if (isPositionIdBlank) {
    positionIdError = "Position ID is required";
  } else if (isPositionIdDuplicate) {
    positionIdError = "Position ID already exists";
  }

  const commitPositionId = React.useCallback(() => {
    const nextId = positionIdDraft.trim();
    const previousId = positionId;
    const currentPosition =
      currentVersion?.getSelectedPosition(selectedPositionId) ?? null;

    if (positionIdError) {
      return;
    }

    if (!currentPosition || nextId === previousId) {
      return;
    }

    const version = currentVersion;

    if (!version) {
      return;
    }

    flushSync(() => {
      currentPosition.id = nextId;
      version.replacePositionIdInStyleAssignments(previousId, nextId);
      currentPosition.techpack = {
        ...(currentPosition.techpack ?? {}),
        position: nextId,
      };
    });
    selectPositions(nextId);
  }, [
    positionId,
    positionIdDraft,
    positionIdError,
    selectedPositionId,
    selectPositions,
    currentVersion,
  ]);

  if (!hasSelectedPosition) {
    return null;
  }

  return (
    <>
      <Typography variant="h2" sx={{ mb: 2 }}>
        {positionName || positionId}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 1.5,
        }}
      >
        <SpecsInputField
          label="Position ID"
          debugPath={
            selectedPositionPath ? `${selectedPositionPath}.id` : undefined
          }
          size="small"
          value={positionIdDraft}
          error={!!positionIdError}
          helperText={positionIdError || " "}
          onChange={(e) => {
            setPositionIdDraft(e.target.value);
          }}
          onBlur={() => {
            commitPositionId();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitPositionId();
            }
          }}
        />
        <SpecsModelInputField
          label="Position Name"
          debugPath={
            selectedPositionPath
              ? `${selectedPositionPath}.designState.positionName`
              : undefined
          }
          size="small"
          selector={(model) =>
            getSelectedPositionForVersion(
              model,
              productVersion,
              selectedPositionId
            )?.designState?.positionName ?? ""
          }
          setValue={(_model, nextValue) =>
            mutateSelectedPosition((position) => {
              position.designState = {
                ...(position.designState ?? {}),
                positionName: nextValue,
              };
            })
          }
        />
        <SpecsModelAutocompleteField
          label="Nom du groupe"
          debugPath={
            selectedPositionPath
              ? `${selectedPositionPath}.designState.groupName`
              : undefined
          }
          options={groupNameOptions}
          placeholder="Sélectionner ou saisir un groupe"
          selector={(model) =>
            getSelectedPositionForVersion(
              model,
              productVersion,
              selectedPositionId
            )?.designState?.groupName ?? ""
          }
          setValue={(_model, nextValue) =>
            mutateSelectedPosition((position) => {
              position.designState = {
                ...(position.designState ?? {}),
                groupName: nextValue,
              };
            })
          }
        />
        <SpecsModelAutocompleteField
          label="Libellé du sélecteur de décoration dans le studio"
          debugPath={
            selectedPositionPath
              ? `${selectedPositionPath}.designState.dropdownLabel`
              : undefined
          }
          options={dropdownLabelOptions}
          placeholder="Sélectionner ou saisir un libellé"
          selector={(model) =>
            getSelectedPositionForVersion(
              model,
              productVersion,
              selectedPositionId
            )?.designState?.dropdownLabel ?? ""
          }
          setValue={(_model, nextValue) =>
            mutateSelectedPosition((position) => {
              position.designState = {
                ...(position.designState ?? {}),
                dropdownLabel: nextValue,
              };
            })
          }
        />
        <SpecsModelMultiAutocompleteField
          label="Allowed Decoration Types"
          debugPath={
            selectedPositionPath
              ? `${selectedPositionPath}.allowedDecorationTypes`
              : undefined
          }
          options={decorationTypeOptions}
          placeholder="Sélectionner ou saisir des types de décorations"
          selector={(model) =>
            getSelectedPositionForVersion(
              model,
              productVersion,
              selectedPositionId
            )?.allowedDecorationTypes ?? []
          }
          setValue={(_model, nextValues) =>
            mutateSelectedPosition((position) => {
              position.allowedDecorationTypes = nextValues;
            })
          }
        />
        <SpecsModelMultiAutocompleteField
          label="Types de décorations deprecated"
          debugPath={
            selectedPositionPath
              ? `${selectedPositionPath}.deprecatedDecorationTypes`
              : undefined
          }
          options={decorationTypeOptions}
          placeholder="Sélectionner ou saisir des types de décorations"
          selector={(model) =>
            getSelectedPositionForVersion(
              model,
              productVersion,
              selectedPositionId
            )?.deprecatedDecorationTypes ?? []
          }
          setValue={(_model, nextValues) =>
            mutateSelectedPosition((position) => {
              position.deprecatedDecorationTypes = nextValues;
            })
          }
        />
      </Box>

      <Typography variant="subtitle2">Design State</Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 1.5,
        }}
      >
        {DESIGN_STATE_FIELDS.map(([label, key]) => (
          <SpecsModelNumberField
            key={key}
            label={label}
            debugPath={
              selectedPositionPath
                ? `${selectedPositionPath}.designState.${key}`
                : undefined
            }
            size="small"
            selector={(model) =>
              getSelectedPositionForVersion(
                model,
                productVersion,
                selectedPositionId
              )?.designState?.[key] ?? 0
            }
            setValue={(_model, nextValue) =>
              mutateSelectedPosition((position, version) => {
                position.designState = {
                  ...(position.designState ?? {}),
                  [key]: nextValue,
                };
                if (key === "maxWidthInches") {
                  position.generator = {
                    ...(position.generator ?? {}),
                    boxWidthInches: nextValue,
                  };
                  syncLockedSideDimension(version, position, "w", nextValue);
                } else if (key === "maxHeightInches") {
                  position.generator = {
                    ...(position.generator ?? {}),
                    boxHeightInches: nextValue,
                  };
                  syncLockedSideDimension(version, position, "h", nextValue);
                }
              })
            }
          />
        ))}
      </Box>
    </>
  );
}
